import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiSave, FiRefreshCw, FiHelpCircle, FiArrowUp, FiArrowDown, FiArrowLeft, FiArrowRight } = FiIcons;

const GameEngine = ({ adventureData, onReturnToMenu }) => {
  const initializedRef = useRef(false);
  const [showMenu, setShowMenu] = useState(false);
  const [colorScheme, setColorScheme] = useState(() => {
    try { return localStorage.getItem('adventure-color-scheme') || 'default'; } catch (e) { return 'default'; }
  });

  const [gameState, setGameState] = useState({
    currentRoom: adventureData.startRoom,
    inventory: [],
    score: 0,
    turns: 0,
    lampLit: false,
    gameFlags: { ...adventureData.gameFlags },
    hintLevel: {}
  });

  const [gameOutput, setGameOutput] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const outputRef = useRef(null);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    initializeGame();
  }, []);

  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-theme', colorScheme);
      localStorage.setItem('adventure-color-scheme', colorScheme);
    } catch (e) {
      // ignore if no DOM
    }
  }, [colorScheme]);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [gameOutput]);

  const initializeGame = () => {
    // Only add welcome once (guarded by initializedRef in the effect)
    addOutput(`Welcome to ${adventureData.title}!`, 'success');
    addOutput(adventureData.description);
    addOutput("Type 'help' for instructions or start exploring!");
    addOutput("");
    lookCommand();
  };

  const saveGame = () => {
    try {
      const key = `adventure-save-${adventureData.title.replace(/\s+/g, '_')}`;
      const payload = {
        adventureTitle: adventureData.title,
        savedAt: new Date().toISOString(),
        gameState
      };
      localStorage.setItem(key, JSON.stringify(payload));
      addOutput('Game saved.', 'success');
    } catch (err) {
      console.error('Save failed', err);
      addOutput('Failed to save game.', 'error');
    }
  };

  const loadGame = () => {
    try {
      const key = `adventure-save-${adventureData.title.replace(/\s+/g, '_')}`;
      const raw = localStorage.getItem(key);
      if (!raw) {
        addOutput('No saved game found for this adventure.', 'error');
        return;
      }
      const payload = JSON.parse(raw);
      if (!payload || !payload.gameState) {
        addOutput('Saved data is invalid or corrupted.', 'error');
        return;
      }

      const loadedState = payload.gameState;
      setGameState(loadedState);
      addOutput('Game loaded.', 'success');
      // Delay a tick so state updates before performing look
      setTimeout(() => lookCommand(loadedState.currentRoom), 0);
    } catch (err) {
      console.error('Load failed', err);
      addOutput('Failed to load saved game.', 'error');
    }
  };

  const addOutput = (text, className = '') => {
    setGameOutput(prev => [...prev, { text, className, id: Date.now() + Math.random() }]);
  };

  const processCommand = (command) => {
    const words = command.toLowerCase().trim().split(' ');
    const verb = words[0];
    const noun = words.slice(1).join(' ');

    addOutput(`> ${command}`, 'prompt');
    
    // Handle movement
    if (['north', 'n', 'south', 's', 'east', 'e', 'west', 'w', 'up', 'u', 'down', 'd'].includes(verb)) {
      const direction = verb.length === 1 ? 
        { n: 'north', s: 'south', e: 'east', w: 'west', u: 'up', d: 'down' }[verb] || verb : verb;
      movePlayer(direction);
      return;
    }

    // Handle other commands
    switch(verb) {
      case 'look':
      case 'l':
        if (noun) {
          examineCommand(noun);
        } else {
          lookCommand();
        }
        break;
      case 'examine':
      case 'x':
        examineCommand(noun);
        break;
      case 'take':
      case 'get':
      case 'pick':
        takeCommand(noun);
        break;
      case 'drop':
        dropCommand(noun);
        break;
      case 'inventory':
      case 'i':
      case 'inv':
        inventoryCommand();
        break;
      case 'use':
        handleUseCommand(noun);
        break;
      case 'read':
        readCommand(noun);
        break;
      case 'light':
        lightCommand(noun);
        break;
      case 'help':
        helpCommand();
        break;
      case 'hint':
        hintCommand();
        break;
      case 'score':
        addOutput(`Your current score is ${gameState.score} points.`);
        break;
      default:
        addOutput("I don't understand that command.", 'error');
    }

    setGameState(prev => ({ ...prev, turns: prev.turns + 1 }));
  };

  const movePlayer = (direction) => {
    const currentRoom = adventureData.rooms[gameState.currentRoom];
    const nextRoomId = currentRoom.exits[direction];

    if (!nextRoomId) {
      addOutput("You can't go that way.", 'error');
      return;
    }

    const nextRoom = adventureData.rooms[nextRoomId];

    if (nextRoom.locked) {
      addOutput("The way is blocked.", 'error');
      return;
    }

    if (nextRoom.dark && !gameState.lampLit) {
      addOutput("It's too dark to see! You need a light source.", 'error');
      return;
    }

    setGameState(prev => ({ ...prev, currentRoom: nextRoomId }));
    lookCommand(nextRoomId);
  };

  const lookCommand = (roomId = null) => {
    const room = adventureData.rooms[roomId || gameState.currentRoom];
    
    if (room.dark && !gameState.lampLit) {
      addOutput("It's pitch black! You can't see anything.", 'error');
      return;
    }

    addOutput(room.name, 'room-name');
    addOutput(room.description, 'room-desc');

    // Show items
    const visibleItems = room.items?.filter(itemId => {
      const item = adventureData.items[itemId];
      return item && !item.hidden;
    }) || [];

    if (visibleItems.length > 0) {
      const itemNames = visibleItems.map(itemId => adventureData.items[itemId].name);
      addOutput(`You can see: ${itemNames.join(', ')}`);
    }

    // Show exits
    const exits = Object.keys(room.exits || {});
    if (exits.length > 0) {
      addOutput(`Exits: ${exits.join(', ')}`, 'exits');
    }

    room.visited = true;
  };

  const examineCommand = (noun) => {
    if (!noun) {
      lookCommand();
      return;
    }

    // Check inventory first
    const inventoryItem = findItemInInventory(noun);
    if (inventoryItem) {
      addOutput(adventureData.items[inventoryItem].description);
      return;
    }

    // Check current room
    const roomItem = findItemInRoom(noun);
    if (roomItem) {
      addOutput(adventureData.items[roomItem].description);
      return;
    }

    addOutput("You don't see that here.", 'error');
  };

  const takeCommand = (noun) => {
    if (noun === 'all') {
      takeAllCommand();
      return;
    }

    if (!noun) {
      addOutput("Take what?", 'error');
      return;
    }

    const room = adventureData.rooms[gameState.currentRoom];
    const itemId = findItemInRoom(noun);

    if (!itemId) {
      addOutput("You don't see that here.", 'error');
      return;
    }

    const item = adventureData.items[itemId];

    if (!item.portable) {
      addOutput("You can't take that.", 'error');
      return;
    }

    if (gameState.inventory.length >= 10) {
      addOutput("You're carrying too much!", 'error');
      return;
    }

    // Remove from room and add to inventory
    room.items = room.items.filter(id => id !== itemId);
    setGameState(prev => ({
      ...prev,
      inventory: [...prev.inventory, itemId],
      score: prev.score + (item.treasure ? (item.value || 10) : 0)
    }));

    addOutput(`Taken: ${item.name}`, 'success');
  };

  const takeAllCommand = () => {
    const room = adventureData.rooms[gameState.currentRoom];
    const portableItems = room.items?.filter(itemId => {
      const item = adventureData.items[itemId];
      return item && item.portable && !item.hidden;
    }) || [];

    if (portableItems.length === 0) {
      addOutput("There's nothing here to take.");
      return;
    }

    let taken = 0;
    let scoreGain = 0;

    portableItems.forEach(itemId => {
      if (gameState.inventory.length + taken < 10) {
        const item = adventureData.items[itemId];
        room.items = room.items.filter(id => id !== itemId);
        addOutput(`Taken: ${item.name}`, 'success');
        taken++;
        if (item.treasure) {
          scoreGain += item.value || 10;
        }
      }
    });

    setGameState(prev => ({
      ...prev,
      inventory: [...prev.inventory, ...portableItems.slice(0, taken)],
      score: prev.score + scoreGain
    }));

    if (taken < portableItems.length) {
      addOutput("You couldn't carry everything.", 'error');
    }
  };

  const dropCommand = (noun) => {
    if (!noun) {
      addOutput("Drop what?", 'error');
      return;
    }

    const itemId = findItemInInventory(noun);
    if (!itemId) {
      addOutput("You're not carrying that.", 'error');
      return;
    }

    const item = adventureData.items[itemId];
    const room = adventureData.rooms[gameState.currentRoom];
    
    room.items = room.items || [];
    room.items.push(itemId);
    
    setGameState(prev => ({
      ...prev,
      inventory: prev.inventory.filter(id => id !== itemId)
    }));

    addOutput(`Dropped: ${item.name}`, 'success');
  };

  const inventoryCommand = () => {
    if (gameState.inventory.length === 0) {
      addOutput("You're not carrying anything.");
      return;
    }

    const itemNames = gameState.inventory.map(itemId => adventureData.items[itemId].name);
    addOutput("You are carrying:");
    itemNames.forEach(name => addOutput(`  ${name}`));
  };

  const handleUseCommand = (noun) => {
    if (!noun) {
      addOutput("Use what?", 'error');
      return;
    }

    const itemId = findItemInInventory(noun);
    if (!itemId) {
      addOutput("You don't have that.", 'error');
      return;
    }

    // Handle special puzzle items
    const currentRoom = adventureData.rooms[gameState.currentRoom];
    if (currentRoom.special && adventureData.specialPuzzles[currentRoom.special]) {
      const puzzle = adventureData.specialPuzzles[currentRoom.special];
      if (puzzle.solution === `use ${noun}` || puzzle.solution === noun) {
        solvePuzzle(puzzle, currentRoom.special);
        return;
      }
    }

    // Handle lighting items
    if (noun.includes('lamp') || noun.includes('torch')) {
      lightCommand(noun);
      return;
    }

    addOutput("You're not sure how to use that here.");
  };

  const lightCommand = (noun) => {
    const lampId = findItemInInventory('lamp') || findItemInInventory('torch');
    if (!lampId) {
      addOutput("You don't have a light source.", 'error');
      return;
    }

    const lamp = adventureData.items[lampId];
    
    if (lamp.magical || lamp.eternal) {
      lamp.lit = true;
      setGameState(prev => ({ ...prev, lampLit: true }));
      addOutput("The light source glows brightly!", 'success');
      return;
    }

    const matchesId = findItemInInventory('matches');
    if (!matchesId) {
      addOutput("You need matches to light that.", 'error');
      return;
    }

    const matches = adventureData.items[matchesId];
    if (matches.uses <= 0) {
      addOutput("You're out of matches.", 'error');
      return;
    }

    matches.uses--;
    lamp.lit = true;
    setGameState(prev => ({ ...prev, lampLit: true }));
    addOutput("The lamp flickers to life!", 'success');

    if (matches.uses === 0) {
      addOutput("That was your last match!", 'error');
    }
  };

  const readCommand = (noun) => {
    const itemId = findItemInInventory(noun) || findItemInRoom(noun);
    if (!itemId) {
      addOutput("You don't see that here.", 'error');
      return;
    }

    const item = adventureData.items[itemId];
    if (!item.readable) {
      addOutput("You can't read that.", 'error');
      return;
    }

    addOutput(item.content);
  };

  const solvePuzzle = (puzzle, puzzleId) => {
    addOutput(puzzle.success, 'success');
    
    if (puzzle.unlocks) {
      const unlockedRoom = adventureData.rooms[puzzle.unlocks];
      if (unlockedRoom) {
        unlockedRoom.locked = false;
      }
    }

    setGameState(prev => ({
      ...prev,
      score: prev.score + (puzzle.points || 0),
      gameFlags: { ...prev.gameFlags, [puzzleId]: true }
    }));
  };

  const hintCommand = () => {
    const hints = [
      "Explore thoroughly and examine everything you find.",
      "Some puzzles require specific items to solve.",
      "Don't forget to read any books or notes you discover.",
      "Try using items in rooms where they might be relevant.",
      "Look for clues in room descriptions and item details."
    ];
    
    const randomHint = hints[Math.floor(Math.random() * hints.length)];
    addOutput(randomHint, 'hint');
    
    setGameState(prev => ({
      ...prev,
      score: Math.max(0, prev.score - 2)
    }));
    addOutput("(Hint penalty: -2 points)", 'error');
  };

  const helpCommand = () => {
    addOutput("Available commands:");
    addOutput("Movement: north/n, south/s, east/e, west/w, up/u, down/d");
    addOutput("Actions: look, examine [item], take [item], drop [item], inventory/i");
    addOutput("Objects: use [item], light [lamp], read [book]");
    addOutput("Game: hint, score, help");
  };

  const findItemInInventory = (itemName) => {
    return gameState.inventory.find(itemId => {
      const item = adventureData.items[itemId];
      return item && item.name.toLowerCase().includes(itemName.toLowerCase());
    });
  };

  const findItemInRoom = (itemName) => {
    const room = adventureData.rooms[gameState.currentRoom];
    return room.items?.find(itemId => {
      const item = adventureData.items[itemId];
      return item && item.name.toLowerCase().includes(itemName.toLowerCase()) && !item.hidden;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      processCommand(inputValue.trim());
      setInputValue('');
    }
  };

  const sendQuickCommand = (command) => {
    processCommand(command);
  };

  return (
    <div className="h-screen flex flex-col bg-slate-900 text-slate-100 font-mono">
      {/* In-game Menu Modal */}
      {showMenu && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="panel rounded-lg p-6 w-96 border border-slate-700">
            <h2 className="text-xl font-bold mb-4">Game Menu</h2>
            <p className="text-sm text-slate-300 mb-4">You can resume your current game, save progress, or return to the main menu.</p>
            <div className="mb-4">
              <label htmlFor="theme-select" className="block text-sm mb-1">Color scheme</label>
              <div className="flex items-center gap-3">
                <select
                  id="theme-select"
                  value={colorScheme}
                  onChange={(e) => setColorScheme(e.target.value)}
                  className="px-2 py-1 rounded themed-button w-full"
                >
                  <option value="default">Default</option>
                  <option value="comfort">Comfort (easier on eyes)</option>
                </select>
                <div className="w-10 h-6 rounded border" style={{ background: 'var(--accent)' }} aria-hidden="true" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowMenu(false)} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded">Resume</button>
              <button onClick={saveGame} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded">Save</button>
              <button onClick={loadGame} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded">Load</button>
              <button onClick={onReturnToMenu} className="px-3 py-2 bg-red-600 hover:bg-red-500 rounded text-white">Exit</button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
        <div className="panel border-b border-slate-700 p-3 flex justify-between items-center">
        <h1 className="text-lg font-bold accent">{adventureData.title}</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowMenu(true)}
            className="bg-slate-700 hover:bg-slate-600 px-3 py-1 rounded text-sm flex items-center gap-1 themed-button"
          >
            <SafeIcon icon={FiHome} />
            Menu
          </button>
          <button onClick={saveGame} className="bg-slate-700 hover:bg-slate-600 px-3 py-1 rounded text-sm flex items-center gap-1 themed-button">
            <SafeIcon icon={FiSave} />
            Save
          </button>
          <button 
            onClick={() => window.location.reload()}
            className="bg-slate-700 hover:bg-slate-600 px-3 py-1 rounded text-sm flex items-center gap-1 themed-button"
          >
            <SafeIcon icon={FiRefreshCw} />
            Restart
          </button>
        </div>
      </div>

      {/* Game Output */}
      <div 
        ref={outputRef}
        className="flex-1 overflow-y-auto p-4 space-y-2 panel"
      >
        {gameOutput.map((line) => (
          <div
            key={line.id}
            className={`${
              line.className === 'room-name' ? 'text-yellow-400 font-bold text-lg' :
              line.className === 'room-desc' ? 'text-blue-300' :
              line.className === 'exits' ? 'text-green-400 text-sm' :
              line.className === 'prompt' ? 'text-yellow-400' :
              line.className === 'error' ? 'text-red-400' :
              line.className === 'success' ? 'text-green-400' :
              line.className === 'hint' ? 'text-orange-400 italic' :
              'text-slate-300'
            }`}
          >
            {line.text}
          </div>
        ))}
      </div>

      {/* Status Bar */}
      <div className="panel border-t border-slate-700 px-4 py-2 flex justify-between text-sm">
        <span>Score: {gameState.score} | Turns: {gameState.turns}</span>
        <span>Items: {gameState.inventory.length}/10</span>
      </div>

      {/* Controls */}
      <div className="bg-slate-800 border-t border-slate-700 p-4 space-y-3">
        {/* Direction Pad */}
    <div className="grid grid-cols-3 gap-2 max-w-48 mx-auto">
          <div></div>
          <button 
            onClick={() => sendQuickCommand('north')}
      className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs flex items-center justify-center font-bold themed-button"
            aria-label="north"
          >
            N
          </button>
          <div></div>
          <button 
            onClick={() => sendQuickCommand('west')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs flex items-center justify-center font-bold themed-button"
            aria-label="west"
          >
            W
          </button>
          <button 
            onClick={() => sendQuickCommand('look')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs themed-button"
          >
            LOOK
          </button>
          <button 
            onClick={() => sendQuickCommand('east')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs flex items-center justify-center font-bold themed-button"
            aria-label="east"
          >
            E
          </button>
          <button 
            onClick={() => sendQuickCommand('up')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs font-semibold themed-button"
          >
            UP
          </button>
          <button 
            onClick={() => sendQuickCommand('south')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs flex items-center justify-center font-bold themed-button"
            aria-label="south"
          >
            S
          </button>
          <button 
            onClick={() => sendQuickCommand('down')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs font-semibold themed-button"
          >
            DOWN
          </button>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-2">
          <button 
            onClick={() => sendQuickCommand('inventory')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs"
          >
            INV
          </button>
          <button 
            onClick={() => sendQuickCommand('take all')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs"
          >
            TAKE ALL
          </button>
          <button 
            onClick={() => sendQuickCommand('hint')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs"
          >
            HINT
          </button>
          <button 
            onClick={() => sendQuickCommand('help')}
            className="bg-slate-700 hover:bg-slate-600 p-2 rounded text-xs flex items-center justify-center"
          >
            <SafeIcon icon={FiHelpCircle} />
          </button>
        </div>

        {/* Command Input */}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter command..."
            className="flex-1 bg-slate-700 border border-slate-600 rounded px-3 py-2 text-slate-100 placeholder-slate-400 focus:outline-none focus:border-blue-400"
            autoComplete="off"
          />
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded font-semibold"
          >
            GO
          </button>
        </form>
      </div>
    </div>
  );
};

export default GameEngine;